"use client";

import React from "react";
import Link from "next/link";
import {
  ArrowLeft,
  Printer,
  Target,
  Users,
  ListChecks,
  BookOpen,
  GraduationCap,
  ClipboardCheck,
  Award,
  Wallet,
  CalendarDays,
  UserRound,
  Clock3,
  MapPin,
  Tag,
  Hash,
  CheckCircle2,
  BadgeCheck,
  Landmark,
} from "lucide-react";
import Button from "../ui/Button";
import RichText from "../Common/RichText";
import type { IFormationDetail } from "@/types/formation";
import {
  MODALITY_LABELS,
  METHOD_LABELS,
  EVALUATION_LABELS,
  FUNDING_LABELS,
  LEVEL_LABELS,
} from "@/constants/formation";
import { formatEuro, formatFrenchDate } from "@/lib/utils";

interface ProgramDocumentProps {
  formation: IFormationDetail;
}

// ── Shared building blocks ────────────────────────────────────────────────────

type ChipTone = "cyan" | "navy" | "orange" | "muted";

const CHIP_TONES: Record<ChipTone, string> = {
  cyan: "bg-[#C7F8FF] text-[#1B365E]",
  navy: "bg-[#1B365E] text-white",
  orange: "bg-[#FFE4B4] text-[#1B515E]",
  muted: "bg-white text-[#1B515E] border border-[#EFEFEF]",
};

function Chip({
  children,
  tone = "cyan",
}: {
  children: React.ReactNode;
  tone?: ChipTone;
}) {
  return (
    <span
      className={`inline-flex items-center gap-1.5 rounded-full px-3 py-1 text-[12px] font-medium leading-5 ${CHIP_TONES[tone]}`}
    >
      {children}
    </span>
  );
}

/** A titled information card — the repeating unit of the document. */
function Section({
  icon: Icon,
  title,
  children,
}: {
  icon: React.ElementType;
  title: string;
  children: React.ReactNode;
}) {
  return (
    <section className="bg-white rounded-2xl border border-[#C7F8FF] p-5 md:p-8 break-inside-avoid">
      <header className="flex items-center gap-3 mb-4 md:mb-5 pb-3 border-b border-[#EFEFEF]">
        <span className="w-10 h-10 rounded-full bg-[#C7F8FF] flex items-center justify-center shrink-0">
          <Icon className="w-5 h-5 text-[#1B365E]" aria-hidden="true" />
        </span>
        <h2 className="font-red-hat font-light text-[22px] md:text-[28px] leading-tight text-[#1B365E]">
          {title}
        </h2>
      </header>
      {children}
    </section>
  );
}

function BulletList({ items }: { items: string[] }) {
  return (
    <ul className="space-y-2.5">
      {items.map((item, i) => (
        <li
          key={i}
          className="flex gap-3 text-[14px] md:text-[15px] leading-6 text-[#1D1D1D]"
        >
          <CheckCircle2
            className="w-4.5 h-4.5 text-[#40BFDE] shrink-0 mt-0.5"
            aria-hidden="true"
          />
          <span>{item}</span>
        </li>
      ))}
    </ul>
  );
}

function Paragraph({ children }: { children: React.ReactNode }) {
  return (
    <p className="text-[14px] md:text-[15px] leading-7 text-[#1D1D1D] whitespace-pre-line">
      {children}
    </p>
  );
}

// ── Document ──────────────────────────────────────────────────────────────────

const ProgramDocument: React.FC<ProgramDocumentProps> = ({ formation }) => {
  // ── Derived, presentation-ready values ──────────────────────────────────────
  const formatLabel = formation.modality?.length
    ? formation.modality.map((m) => MODALITY_LABELS[m] ?? m).join(" / ")
    : "";
  const levelLabel = LEVEL_LABELS[formation.level] ?? "";

  const durationValue = formation.duration_days
    ? `${formation.duration_days} j • ${formation.duration_hours} h`
    : formation.duration_hours
      ? `${formation.duration_hours} heures`
      : (formation.duration ?? "");

  // Tarifs affichés HT : la cible est professionnelle (entreprises / indépendants).
  const individualPrice = formation.pricing?.individual?.price_ht ?? null;
  const groupPrice = formation.pricing?.group?.price_ht ?? null;
  const priceValue =
    individualPrice !== null
      ? `${formatEuro(individualPrice)} HT`
      : formation.price || "Sur devis";

  const fundingLabels = (formation.funding_types ?? []).map(
    (f) => FUNDING_LABELS[f] ?? f,
  );
  const methodLabels = (formation.methods ?? []).map(
    (m) => METHOD_LABELS[m] ?? m,
  );
  const evaluationLabels = (formation.evaluation_methods ?? []).map(
    (e) => EVALUATION_LABELS[e] ?? e,
  );

  const modules = formation.program?.modules ?? [];
  const datedSessions = (formation.sessions ?? []).filter((s) => s.start_date);
  const hasObjectives = formation.specific_objectives?.length > 0;
  const hasTrainer =
    formation.has_trainer_info &&
    Boolean(formation.trainer_name || formation.trainers?.length);
  const hasPricing =
    individualPrice !== null || groupPrice !== null || fundingLabels.length > 0;

  // ── Header metadata tiles ───────────────────────────────────────────────────
  type Tile = { icon: React.ElementType; label: string; value: string };
  const metaTiles: Tile[] = [];
  if (formatLabel)
    metaTiles.push({ icon: GraduationCap, label: "Format", value: formatLabel });
  if (durationValue)
    metaTiles.push({ icon: Clock3, label: "Durée", value: durationValue });
  if (priceValue)
    metaTiles.push({ icon: Wallet, label: "Tarif", value: priceValue });
  if (levelLabel)
    metaTiles.push({ icon: Award, label: "Niveau", value: levelLabel });
  if (formation.reference)
    metaTiles.push({ icon: Hash, label: "Référence", value: formation.reference });
  if (formation.location && formation.location !== "N/A")
    metaTiles.push({ icon: MapPin, label: "Lieu", value: formation.location });

  return (
    <div className="bg-[#EFEFEF] print:bg-white">
      <div className="max-w-5xl mx-auto px-4 md:px-6 py-8 md:py-12 space-y-6 md:space-y-8">
        {/* Toolbar (excluded from print) */}
        <div className="flex items-center justify-between gap-4 print:hidden">
          <Link
            href={`/formation/${formation.slug}`}
            className="inline-flex items-center gap-2 text-[14px] font-bold text-[#1B365E] hover:text-[#40BFDE] transition-colors"
          >
            <ArrowLeft className="w-4 h-4" aria-hidden="true" />
            Retour à la formation
          </Link>
          <button
            type="button"
            onClick={() => window.print()}
            className="inline-flex items-center gap-2 rounded-lg border border-[#1B365E] px-4 h-10 text-[14px] font-bold text-[#1B365E] hover:bg-[#1B365E] hover:text-white transition-colors cursor-pointer"
          >
            <Printer className="w-4 h-4" aria-hidden="true" />
            Imprimer / PDF
          </button>
        </div>

        {/* Header / hero */}
        <header className="bg-white rounded-2xl border border-[#C7F8FF] p-6 md:p-10 break-inside-avoid">
          <p className="text-[11px] md:text-[12px] font-bold uppercase tracking-[1.5px] text-[#40BFDE] mb-3">
            Programme de formation
          </p>

          <div className="flex flex-wrap gap-2 mb-4">
            {formation.category && (
              <Chip tone="cyan">
                <Tag className="w-3.5 h-3.5" aria-hidden="true" />
                {formation.category}
              </Chip>
            )}
            {levelLabel && <Chip tone="muted">{levelLabel}</Chip>}
            {formation.isNew && <Chip tone="orange">Nouveau</Chip>}
            {fundingLabels.length > 0 && (
              <Chip tone="navy">100% finançable</Chip>
            )}
          </div>

          <h1 className="font-red-hat font-light text-[28px] md:text-[44px] leading-tight text-[#1B365E] mb-4">
            {formation.title}
          </h1>

          {formation.description && (
            <p className="text-[14px] md:text-[16px] leading-7 text-[#1D1D1D] max-w-3xl">
              {formation.description}
            </p>
          )}

          {metaTiles.length > 0 && (
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mt-6 pt-6 border-t border-[#EFEFEF]">
              {metaTiles.map((tile) => (
                <div key={tile.label} className="flex items-center gap-3">
                  <span className="w-9 h-9 rounded-full bg-[#C7F8FF] flex items-center justify-center shrink-0">
                    <tile.icon
                      className="w-4.5 h-4.5 text-[#1B365E]"
                      aria-hidden="true"
                    />
                  </span>
                  <div className="min-w-0">
                    <p className="text-[11px] uppercase tracking-wide text-[#676767]">
                      {tile.label}
                    </p>
                    <p className="text-[13px] md:text-[14px] font-bold text-[#1B365E] truncate">
                      {tile.value}
                    </p>
                  </div>
                </div>
              ))}
            </div>
          )}
        </header>

        {/* Objectifs pédagogiques */}
        {hasObjectives && (
          <Section icon={Target} title="Objectifs pédagogiques">
            <BulletList items={formation.specific_objectives} />
          </Section>
        )}

        {/* À l'issue de la formation (takeaways) */}
        {formation.takeaways?.length > 0 && (
          <Section icon={BadgeCheck} title="Compétences visées">
            <BulletList items={formation.takeaways} />
          </Section>
        )}

        {/* Public visé */}
        {formation.target_audience && (
          <Section icon={Users} title="Public concerné">
            <RichText
              html={formation.target_audience}
              className="text-[14px] md:text-[15px] leading-7 text-[#1D1D1D] wrap-break-word"
            />
          </Section>
        )}

        {/* Prérequis */}
        {formation.prerequisites && (
          <Section icon={ListChecks} title="Prérequis">
            <RichText
              html={formation.prerequisites}
              className="text-[14px] md:text-[15px] leading-7 text-[#1D1D1D] wrap-break-word"
            />
          </Section>
        )}

        {/* Programme détaillé */}
        {modules.length > 0 && (
          <Section icon={BookOpen} title="Contenu de la formation">
            <ol className="space-y-4">
              {modules.map((module, i) => (
                <li
                  key={i}
                  className="rounded-lg bg-[#EFEFEF] p-4 md:p-5 break-inside-avoid"
                >
                  <div className="flex items-start gap-3">
                    <span className="w-7 h-7 rounded-full bg-[#1B365E] text-white text-[13px] font-bold flex items-center justify-center shrink-0">
                      {i + 1}
                    </span>
                    <div className="flex-1 min-w-0">
                      <div className="flex flex-wrap items-center gap-2">
                        <h3 className="text-[14px] md:text-[16px] font-bold text-[#1D1D1D] leading-5">
                          {module.title}
                        </h3>
                        {module.duration && (
                          <Chip tone="muted">{module.duration}</Chip>
                        )}
                      </div>
                      {module.content && (
                        <p className="mt-2 text-[13px] md:text-[14px] leading-6 text-[#1D1D1D] whitespace-pre-line">
                          {module.content}
                        </p>
                      )}
                    </div>
                  </div>
                </li>
              ))}
            </ol>
          </Section>
        )}

        {/* Méthodes pédagogiques */}
        {(methodLabels.length > 0 || formation.methods_detail) && (
          <Section icon={GraduationCap} title="Méthodes pédagogiques">
            {methodLabels.length > 0 && (
              <div className="flex flex-wrap gap-2 mb-4 last:mb-0">
                {methodLabels.map((label) => (
                  <Chip key={label} tone="cyan">
                    {label}
                  </Chip>
                ))}
              </div>
            )}
            {formation.methods_detail && (
              <RichText
                html={formation.methods_detail}
                className="text-[14px] md:text-[15px] leading-7 text-[#1D1D1D] wrap-break-word"
              />
            )}
          </Section>
        )}

        {/* Modalités d'évaluation */}
        {evaluationLabels.length > 0 && (
          <Section icon={ClipboardCheck} title="Modalités d'évaluation">
            <BulletList items={evaluationLabels} />
          </Section>
        )}

        {/* Certification */}
        {formation.has_certification && (
          <Section icon={Award} title="Certification">
            <Paragraph>
              {formation.certification_name
                ? `Cette formation prépare à la certification « ${formation.certification_name} »${
                    formation.certification_code
                      ? `, enregistrée sous le numéro ${formation.certification_code}.`
                      : "."
                  }`
                : "Cette formation délivre une attestation de fin de formation."}
            </Paragraph>
          </Section>
        )}

        {/* Tarifs & financement */}
        {hasPricing && (
          <Section icon={Wallet} title="Tarifs et financement">
            {(individualPrice !== null || groupPrice !== null) && (
              <div className="grid sm:grid-cols-2 gap-4">
                {individualPrice !== null && (
                  <div className="rounded-lg border border-[#EFEFEF] p-4">
                    <p className="text-[12px] text-[#676767] mb-1">
                      Tarif individuel (inter)
                    </p>
                    <p className="text-[20px] font-bold text-[#1B365E]">
                      {formatEuro(individualPrice)}{" "}
                      <span className="text-[13px] font-medium">HT</span>
                    </p>
                  </div>
                )}
                {groupPrice !== null && (
                  <div className="rounded-lg border border-[#EFEFEF] p-4">
                    <p className="text-[12px] text-[#676767] mb-1">
                      Tarif groupe (intra)
                    </p>
                    <p className="text-[20px] font-bold text-[#1B365E]">
                      {formatEuro(groupPrice)}{" "}
                      <span className="text-[13px] font-medium">HT</span>
                    </p>
                  </div>
                )}
              </div>
            )}
            {fundingLabels.length > 0 && (
              <div className="mt-5">
                <p className="text-[13px] font-semibold text-[#1B365E] mb-2">
                  Financements possibles
                </p>
                <div className="flex flex-wrap gap-2">
                  {fundingLabels.map((label) => (
                    <Chip key={label} tone="cyan">
                      {label}
                    </Chip>
                  ))}
                </div>
              </div>
            )}
          </Section>
        )}

        {/* Dates & lieux */}
        {datedSessions.length > 0 && (
          <Section icon={CalendarDays} title="Prochaines sessions">
            <ul className="divide-y divide-[#EFEFEF]">
              {datedSessions.map((session) => (
                <li
                  key={session.id}
                  className="flex flex-wrap items-center justify-between gap-2 py-3 first:pt-0 last:pb-0"
                >
                  <span className="flex items-center gap-2 text-[14px] font-medium text-[#1B365E]">
                    <CalendarDays
                      className="w-4 h-4 text-[#40BFDE]"
                      aria-hidden="true"
                    />
                    {formatFrenchDate(session.start_date)}
                    {session.end_date
                      ? ` → ${formatFrenchDate(session.end_date)}`
                      : ""}
                  </span>
                  <span className="flex items-center gap-2 text-[13px] text-[#676767]">
                    <MapPin className="w-4 h-4" aria-hidden="true" />
                    {session.location}
                  </span>
                  <span className="text-[14px] font-bold text-[#1B365E]">
                    {session.price !== null
                      ? `${formatEuro(session.price)} HT`
                      : "Sur devis"}
                  </span>
                </li>
              ))}
            </ul>
          </Section>
        )}

        {/* Formateur */}
        {hasTrainer && (
          <Section icon={UserRound} title="Votre formateur">
            {formation.trainer_name && (
              <div className={formation.trainer_bio ? "mb-3" : ""}>
                <p className="text-[16px] font-bold text-[#1B365E]">
                  {formation.trainer_name}
                </p>
                {formation.trainer_title && (
                  <p className="text-[13px] text-[#676767]">
                    {formation.trainer_title}
                  </p>
                )}
              </div>
            )}
            {formation.trainer_bio && <Paragraph>{formation.trainer_bio}</Paragraph>}
          </Section>
        )}

        {/* Organisme (footer) */}
        {formation.center && (
          <section className="bg-[#1B365E] text-white rounded-2xl p-6 md:p-8 break-inside-avoid">
            <div className="flex items-center gap-3 mb-3">
              <span className="w-10 h-10 rounded-full bg-white/10 flex items-center justify-center shrink-0">
                <Landmark className="w-5 h-5 text-[#C7F8FF]" aria-hidden="true" />
              </span>
              <h2 className="font-red-hat font-light text-[22px] md:text-[26px] leading-tight">
                {formation.center.name}
              </h2>
            </div>
            <div className="flex flex-wrap gap-x-8 gap-y-2 text-[13px] text-[#C7F8FF] pl-13">
              {formation.center.siret && (
                <span>SIRET : {formation.center.siret}</span>
              )}
              {formation.center.accreditation_number && (
                <span>
                  N° de déclaration d&apos;activité :{" "}
                  {formation.center.accreditation_number}
                </span>
              )}
            </div>
          </section>
        )}

        {/* CTA (excluded from print) */}
        <div className="flex flex-col sm:flex-row gap-4 justify-center print:hidden">
          <Button
            variant="gradient"
            size="unset"
            className="h-13 px-8 rounded-lg text-[14px]"
          >
            S&apos;inscrire à la formation
          </Button>
          <Button
            variant="outline-gradient"
            size="unset"
            className="h-13 px-8 rounded-lg text-[14px]"
          >
            Demander un devis
          </Button>
        </div>
      </div>
    </div>
  );
};

export default ProgramDocument;
